import type { EventLinkDraft, EventMediaUploadInput } from './schemas';
import { systemNowMs } from './clock';

export const EVENT_MEDIA_BUCKET = 'event-media';
export const MAX_EVENT_LINKS = 5;
export const MAX_EVENT_MEDIA_ITEMS = 10;
export const EVENT_MEDIA_MAX_BYTES = 50 * 1024 * 1024;
export const EVENT_VIDEO_MAX_DURATION_SECONDS = 60;

export type EventMediaType = 'photo' | 'video';

export type { EventLinkDraft, EventMediaUploadInput } from './schemas';

export type NormalizedEventLink = Readonly<{
  label: string;
  sortOrder: number;
  url: string;
}>;

const ACCEPTED_EVENT_IMAGE_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const ACCEPTED_EVENT_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-m4v',
]);

const ACCEPTED_EVENT_IMAGE_EXTENSIONS = new Set(['heic', 'heif', 'jpeg', 'jpg', 'png', 'webp']);
const ACCEPTED_EVENT_VIDEO_EXTENSIONS = new Set(['m4v', 'mov', 'mp4', 'webm']);

const MIME_EXTENSION: Record<string, string> = {
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/x-m4v': 'm4v',
};

function fileExtension(name?: string | null): string {
  return (
    name
      ?.split('.')
      .pop()
      ?.replace(/[^a-z0-9]/gi, '')
      .toLowerCase() ?? ''
  );
}

function normalizedMimeType(type?: string | null): string {
  return type?.toLowerCase().trim() ?? '';
}

function isImageKind(kind?: string | null): boolean {
  return kind === 'image' || kind === 'photo';
}

function isVideoKind(kind?: string | null): boolean {
  return kind === 'video';
}

function isAcceptedImage(input: EventMediaUploadInput): boolean {
  const mimeType = normalizedMimeType(input.type);
  const extension = fileExtension(input.name);

  return (
    isImageKind(input.mediaKind) ||
    ACCEPTED_EVENT_IMAGE_MIME_TYPES.has(mimeType) ||
    ACCEPTED_EVENT_IMAGE_EXTENSIONS.has(extension)
  );
}

function isAcceptedVideo(input: EventMediaUploadInput): boolean {
  const mimeType = normalizedMimeType(input.type);
  const extension = fileExtension(input.name);

  return (
    isVideoKind(input.mediaKind) ||
    ACCEPTED_EVENT_VIDEO_MIME_TYPES.has(mimeType) ||
    ACCEPTED_EVENT_VIDEO_EXTENSIONS.has(extension)
  );
}

export function formatEventMediaUploadLimit(maxBytes = EVENT_MEDIA_MAX_BYTES): string {
  return `${Math.floor(maxBytes / (1024 * 1024))} MB`;
}

const URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const WEB_URL_PATTERN = /^https?:\/\//i;
const WEB_URL_PREFIX_ONLY_PATTERN = /^https?:\/\/$/i;

export function normalizeEventLinkUrl(value: string): string {
  const trimmed = value.trim();

  if (!trimmed || WEB_URL_PREFIX_ONLY_PATTERN.test(trimmed)) {
    return '';
  }

  if (WEB_URL_PATTERN.test(trimmed) || URL_SCHEME_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function eventLinkLabelSuggestion(value: string): string {
  const normalizedUrl = normalizeEventLinkUrl(value);
  if (!normalizedUrl) return '';

  try {
    const parsed = new URL(normalizedUrl);
    const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (
      hostname === 'forms.gle' ||
      (hostname === 'docs.google.com' && parsed.pathname.toLowerCase().startsWith('/forms'))
    ) {
      return 'Google Form';
    }
    return hostname;
  } catch {
    return '';
  }
}

export function normalizeEventLinksDraft(links: readonly EventLinkDraft[]): NormalizedEventLink[] {
  return links
    .map((link, index) => {
      const url = normalizeEventLinkUrl(link.url);
      return {
        label: link.label.trim() || eventLinkLabelSuggestion(url),
        sortOrder: link.sortOrder ?? index,
        url,
      };
    })
    .filter((link) => link.label.length > 0 || link.url.length > 0)
    .map((link, index) => ({ ...link, sortOrder: index }));
}

export function eventLinksValidationError(links: readonly EventLinkDraft[]): string | null {
  const normalized = normalizeEventLinksDraft(links);

  if (normalized.length > MAX_EVENT_LINKS) {
    return `Add up to ${MAX_EVENT_LINKS} additional information links.`;
  }

  for (const link of normalized) {
    if (!link.url) {
      return 'Each additional information link needs a URL.';
    }

    try {
      const parsed = new URL(link.url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return 'Additional information links must start with http:// or https://.';
      }
      if (
        !parsed.hostname.includes('.') ||
        parsed.hostname.startsWith('.') ||
        parsed.hostname.endsWith('.')
      ) {
        return 'Enter a complete domain, such as google.com.';
      }
    } catch {
      return 'Enter a valid domain, such as google.com.';
    }

    if (!link.label) {
      return 'Add a label for each additional information link.';
    }
  }

  return null;
}

export function eventMediaUploadError(input: EventMediaUploadInput): string | null {
  if (input.size != null && input.size > EVENT_MEDIA_MAX_BYTES) {
    return `Event media must be ${formatEventMediaUploadLimit()} or smaller.`;
  }

  if (!isAcceptedImage(input) && !isAcceptedVideo(input)) {
    return 'Event media must be a JPG, PNG, HEIC, HEIF, WebP, MP4, MOV, M4V, or WebM file.';
  }

  if (
    isAcceptedVideo(input) &&
    input.duration != null &&
    input.duration > EVENT_VIDEO_MAX_DURATION_SECONDS
  ) {
    return `Event videos must be ${EVENT_VIDEO_MAX_DURATION_SECONDS} seconds or shorter.`;
  }

  return null;
}

export function eventMediaCountError(totalMediaItems: number): string | null {
  if (totalMediaItems > MAX_EVENT_MEDIA_ITEMS) {
    return `Add up to ${MAX_EVENT_MEDIA_ITEMS} photos or videos.`;
  }

  return null;
}

export function eventMediaTypeFromInput(input: EventMediaUploadInput): EventMediaType {
  if (isAcceptedVideo(input)) {
    return 'video';
  }

  return 'photo';
}

export function eventMediaExtension(input: EventMediaUploadInput): string {
  const extension = fileExtension(input.name);
  const mimeType = normalizedMimeType(input.type);

  if (
    ACCEPTED_EVENT_IMAGE_EXTENSIONS.has(extension) ||
    ACCEPTED_EVENT_VIDEO_EXTENSIONS.has(extension)
  ) {
    return extension === 'jpeg' ? 'jpg' : extension;
  }

  return MIME_EXTENSION[mimeType] ?? (eventMediaTypeFromInput(input) === 'video' ? 'mp4' : 'jpg');
}

export function eventMediaStoragePath(
  input: EventMediaUploadInput &
    Readonly<{
      eventId: string;
      sequence: number;
      tenantId: string;
      timestamp?: number;
    }>
): string {
  const timestamp = input.timestamp ?? systemNowMs();
  const extension = eventMediaExtension(input);

  return `${input.tenantId}/${input.eventId}/${timestamp}-${input.sequence}.${extension}`;
}
